import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n';
import dayjs from 'dayjs';
import 'dayjs/locale/pl';
import { CustomDateTimePicker } from '../../../commons/datepicker';
import '../../../../styles/EditTemporaryLockModal.css';

const EditTemporaryLockModal = ({ isOpen, onClose, lock, onSave }) => {
    const { t } = useTranslation(['calendar', 'common']);
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [error, setError] = useState('');

    // Initialize form with lock data
    useEffect(() => {
        if (isOpen && lock) {
            const start = dayjs(lock.startTime);
            const end = dayjs(lock.endTime);

            // Format for datetime-local input
            setStartTime(start.format('YYYY-MM-DDTHH:mm'));
            setEndTime(end.format('YYYY-MM-DDTHH:mm'));
            setError('');
        }
    }, [isOpen, lock]);

    const handleSave = () => {
        // Validation
        const start = dayjs(startTime);
        const end = dayjs(endTime);

        if (!start.isValid() || !end.isValid()) {
            setError(t('lock.invalidTimeFormat'));
            return;
        }

        if (end.isBefore(start) || end.isSame(start)) {
            setError(t('lock.endBeforeStart'));
            return;
        }

        // Minimum 15 minutes duration
        if (end.diff(start, 'minute') < 15) {
            setError(t('lock.minDuration'));
            return;
        }

        // Call parent save handler with new times
        onSave({
            startTime: start.format('YYYY-MM-DDTHH:mm:ss'),
            endTime: end.format('YYYY-MM-DDTHH:mm:ss')
        });

        onClose();
    };

    const handleStartTimeChange = (e) => {
        const newStartTime = e.target.value;
        setStartTime(newStartTime);
        setError('');

        // Auto-adjust end time if it's before or equal to start time
        const start = dayjs(newStartTime);
        const end = dayjs(endTime);

        if (end.isBefore(start) || end.isSame(start)) {
            // Snap to next 15-minute interval
            const snappedMinutes = Math.ceil(start.minute() / 15) * 15;
            const snappedEndTime = start.minute(snappedMinutes).second(0);
            setEndTime(snappedEndTime.format('YYYY-MM-DDTHH:mm'));
        }
    };

    const handleEndTimeChange = (e) => {
        setEndTime(e.target.value);
        setError('');
    };

    const handleClose = () => {
        setError('');
        onClose();
    };

    if (!isOpen || !lock) return null;

    const lockDate = dayjs(lock.startTime).locale(i18n.language?.startsWith('pl') ? 'pl' : 'en').format('dddd, DD MMMM YYYY');
    const duration = startTime && endTime ? dayjs(endTime).diff(dayjs(startTime), 'minute') : 0;

    return (
        <div className="edit-temp-lock-modal-overlay">
            <div className="edit-temp-lock-modal-content">
                <div className="edit-temp-lock-modal-header">
                    <div className="edit-temp-lock-modal-header-info">
                        <h2 className="edit-temp-lock-modal-title">{t('lock.editLock')}</h2>
                        <div className="edit-temp-lock-date">{lockDate}</div>
                    </div>
                    <button className="edit-temp-lock-close-btn" onClick={handleClose}>✕</button>
                </div>

                <div className="edit-temp-lock-modal-body">
                    {error && (
                        <div className="edit-temp-lock-error-message">
                            {error}
                        </div>
                    )}

                    <div className="edit-temp-lock-form-group">
                        <label htmlFor="start-time" className="edit-temp-lock-label">
                            {t('lock.startTime')}
                        </label>
                        <CustomDateTimePicker
                            id="start-time"
                            name="startTime"
                            value={startTime}
                            onChange={handleStartTimeChange}
                            step={900}
                        />
                    </div>

                    <div className="edit-temp-lock-form-group">
                        <label htmlFor="end-time" className="edit-temp-lock-label">
                            {t('lock.endTime')}
                        </label>
                        <CustomDateTimePicker
                            id="end-time"
                            name="endTime"
                            value={endTime}
                            onChange={handleEndTimeChange}
                            min={startTime}
                            step={900}
                        />
                    </div>

                    {duration > 0 && (
                        <div className="edit-temp-lock-duration-info">
                            {t('lock.durationMinutes', { minutes: duration })}
                        </div>
                    )}
                </div>

                <div className="edit-temp-lock-modal-footer">
                    <button
                        className="edit-temp-lock-cancel-btn"
                        onClick={handleClose}
                    >
                        {t('common:cancel')}
                    </button>
                    <button
                        className="edit-temp-lock-save-btn"
                        onClick={handleSave}
                        disabled={!startTime || !endTime}
                    >
                        {t('common:save')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditTemporaryLockModal;